import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { getCodeType, updateCodeType } from "../../redux/features/clicksSlice";
import { Box, Typography, Button, FormControl, InputLabel, Select, MenuItem, Paper, Grid, Chip, TextField } from "@mui/material";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet";
import MonacoEditor from "@monaco-editor/react";
import SaveIcon from '@mui/icons-material/Save';

const CodeTypeEditor = () => {
  const dispatch = useDispatch();
  const [codeTypes, setCodeTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTypeId, setSelectedTypeId] = useState("");
  const [originalCode, setOriginalCode] = useState("");
  const [description, setDescription] = useState("");
  const [newObCodePrefix, setNewObCodePrefix] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const fetchCodeTypes = async () => {
    try {
      const response = await dispatch(getCodeType());
      if (response.payload) {
        setCodeTypes(response.payload);
        if (response.payload.length > 0 && !selectedTypeId) {
          // Select the first one by default, or leave it blank as requested
          // We'll leave it blank if they want to choose first
        }
      }
    } catch (error) {
      console.error("Failed to load code types", error);
      toast.error("Failed to load code types");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCodeTypes();
  }, [dispatch]);

  const handleSelectChange = (event) => {
    const typeId = event.target.value;
    setSelectedTypeId(typeId);
    const selectedType = codeTypes.find(ct => ct.type_id === typeId);
    if (selectedType) {
      setOriginalCode(selectedType.code || "");
      setDescription(selectedType.description || "");
      setNewObCodePrefix(selectedType.new_ob_code_prefix || "");
    }
  };

  const handleSaveCodeType = async () => {
    if (!selectedTypeId) return;
    setIsSaving(true);
    try {
      await dispatch(updateCodeType({ 
        type_id: selectedTypeId, 
        code: originalCode,
        description,
        new_ob_code_prefix: newObCodePrefix
      })).unwrap();
      toast.success("Code successfully obfuscated and saved!");
      await fetchCodeTypes(); // Refresh to get the latest new_ob_code
    } catch (error) {
      toast.error("Failed to update code type");
    } finally {
      setIsSaving(false);
    }
  };

  const selectedTypeData = codeTypes.find(ct => ct.type_id === selectedTypeId);

  return (
    <Box sx={{ padding: { xs: "8px", sm: "16px" }, minHeight: "100vh", backgroundColor: "#f4f7f9", display: "flex", flexDirection: "column" }}>
      <Helmet>
        <title>Alpha Targeting || Code Type Editor</title>
      </Helmet>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" color="primary.main">
          Code Type Editor
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          Select a code type to edit. Use the full-page editor below to modify the code. Clicking "Save & Obfuscate" will generate the final output.
        </Typography>
      </Box>

      {loading ? (
        <Typography>Loading code types...</Typography>
      ) : (
        <>
          <Paper sx={{ p: 2, mb: 3, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <FormControl sx={{ minWidth: 280 }}>
              <InputLabel>Select Code Type</InputLabel>
              <Select
                value={selectedTypeId}
                label="Select Code Type"
                onChange={handleSelectChange}
              >
                <MenuItem value="" disabled>Select a code type</MenuItem>
                {codeTypes.map(ct => (
                  <MenuItem key={ct.type_id} value={ct.type_id}>
                    {ct.type_id} — {ct.description}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {selectedTypeData && (
              <>
                <TextField 
                  label="Description" 
                  value={description} 
                  onChange={(e) => setDescription(e.target.value)} 
                  size="small"
                  sx={{ flexGrow: 1, minWidth: 180 }}
                />
                <TextField 
                  label="New Ob Code Prefix" 
                  value={newObCodePrefix} 
                  onChange={(e) => setNewObCodePrefix(e.target.value)} 
                  size="small"
                  sx={{ flexGrow: 1, minWidth: 180 }}
                  placeholder="e.g. var _0x..."
                />
                <Chip 
                  label={`Updated: ${new Date(selectedTypeData.last_updated_at).toLocaleString()}`} 
                  size="small" 
                  variant="outlined"
                  sx={{ whiteSpace: "nowrap" }}
                />
                <Button 
                  variant="contained" 
                  color="primary" 
                  startIcon={<SaveIcon />} 
                  onClick={handleSaveCodeType}
                  disabled={isSaving}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {isSaving ? "Saving..." : "Save & Obfuscate"}
                </Button>
              </>
            )}
          </Paper>

          {selectedTypeData ? (
            <Grid container spacing={2} sx={{ flexGrow: 1 }}>
              <Grid item xs={12} md={6} sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: "bold" }}>Original Source Code</Typography>
                <Paper variant="outlined" sx={{ flexGrow: 1, minHeight: "600px", overflow: "hidden", borderRadius: "8px" }}>
                  <MonacoEditor
                    height="100%"
                    language="javascript"
                    theme="vs-dark"
                    value={originalCode}
                    onChange={(value) => setOriginalCode(value)}
                    options={{
                      minimap: { enabled: false },
                      fontSize: 14,
                      wordWrap: "on",
                      scrollBeyondLastLine: false,
                    }}
                  />
                </Paper>
              </Grid>
              <Grid item xs={12} md={6} sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="subtitle2" color="secondary" sx={{ mb: 1, fontWeight: "bold" }}>Obfuscated Output (Read-only)</Typography>
                <Paper variant="outlined" sx={{ flexGrow: 1, minHeight: "600px", overflow: "hidden", borderRadius: "8px" }}>
                  <MonacoEditor
                    height="100%"
                    language="javascript"
                    theme="vs-light"
                    value={selectedTypeData.new_ob_code || "// No obfuscated code generated yet."}
                    options={{
                      readOnly: true,
                      minimap: { enabled: false },
                      fontSize: 14,
                      wordWrap: "on",
                      scrollBeyondLastLine: false,
                    }}
                  />
                </Paper>
              </Grid>
            </Grid>
          ) : (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px", border: "1px dashed #ccc", borderRadius: "8px", backgroundColor: "#fff" }}>
              <Typography variant="h6" color="textSecondary">
                Please select a code type from the dropdown above to begin editing.
              </Typography>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default CodeTypeEditor;
